import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import type { EmailBodyFormat } from "../../types/emailBodyFormat";
import type { NotificationChannel } from "../NotificationChannelToggle/NotificationChannelToggle";

/** Single control: SMS vs plain-text email vs HTML email (maps to `type` + `emailBodyFormat`). */
export type VisitDeliveryMode = "sms" | "plain_email" | "html_email";

export function visitDeliveryModeFromState(
  type: NotificationChannel,
  emailFmt: EmailBodyFormat
): VisitDeliveryMode {
  if (type === "sms") return "sms";
  return emailFmt === "html" ? "html_email" : "plain_email";
}

type ThreeProps = {
  variant: "three";
  value: VisitDeliveryMode;
  onChange: (mode: VisitDeliveryMode) => void;
  className?: string;
};

/** Same three segments as `three`, but SMS is not available (email-only notifications). */
type ThreeSmsDisabledProps = {
  variant: "three_sms_disabled";
  value: EmailBodyFormat;
  onChange: (format: EmailBodyFormat) => void;
  className?: string;
};

export type NotificationDeliveryToggleProps = ThreeProps | ThreeSmsDisabledProps;

const threeModes: VisitDeliveryMode[] = ["sms", "plain_email", "html_email"];

export function NotificationDeliveryToggle(props: NotificationDeliveryToggleProps) {
  const { t } = useTranslation();

  if (props.variant === "three_sms_disabled") {
    const fmt = props.value;
    const smsDisabledTitle = String(t("notifications.delivery.sms_disabled_title"));
    const smsDisabledAria = String(t("notifications.delivery.sms_disabled_aria"));

    return (
      <div
        className={cn(
          "inline-flex flex-wrap rounded-md border border-stone-200 bg-white overflow-hidden max-w-full",
          props.className
        )}
      >
        <button
          type="button"
          disabled
          title={smsDisabledTitle}
          aria-label={smsDisabledAria}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-left border-r border-stone-200",
            "cursor-not-allowed bg-stone-50 text-stone-400"
          )}
        >
          {String(t("notifications.delivery.sms"))}
        </button>
        <button
          type="button"
          title={String(t("notifications.delivery.plain_email_aria"))}
          aria-label={String(t("notifications.delivery.plain_email_aria"))}
          aria-pressed={fmt === "plain"}
          onClick={() => props.onChange("plain")}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors text-left border-r border-stone-200",
            fmt === "plain" ? "bg-stone-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
          )}
        >
          {String(t("notifications.delivery.plain_email"))}
        </button>
        <button
          type="button"
          title={String(t("notifications.delivery.html_email_aria"))}
          aria-label={String(t("notifications.delivery.html_email_aria"))}
          aria-pressed={fmt === "html"}
          onClick={() => props.onChange("html")}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors text-left",
            fmt === "html" ? "bg-stone-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
          )}
        >
          {String(t("notifications.delivery.html_email"))}
        </button>
      </div>
    );
  }

  const label = (m: VisitDeliveryMode) => {
    if (m === "sms") return String(t("notifications.delivery.sms"));
    if (m === "plain_email") return String(t("notifications.delivery.plain_email"));
    return String(t("notifications.delivery.html_email"));
  };

  const aria = (m: VisitDeliveryMode) => {
    if (m === "sms") return String(t("notifications.delivery.sms_aria"));
    if (m === "plain_email") return String(t("notifications.delivery.plain_email_aria"));
    return String(t("notifications.delivery.html_email_aria"));
  };

  return (
    <div
      className={cn(
        "inline-flex flex-wrap rounded-md border border-stone-200 bg-white overflow-hidden max-w-full",
        props.className
      )}
    >
      {threeModes.map((mode, i) => (
        <button
          key={mode}
          type="button"
          title={aria(mode)}
          aria-label={aria(mode)}
          aria-pressed={props.value === mode}
          onClick={() => props.onChange(mode)}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors text-left",
            i > 0 && "border-l border-stone-200",
            props.value === mode ? "bg-stone-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
          )}
        >
          {label(mode)}
        </button>
      ))}
    </div>
  );
}
